import { graphql } from "gatsby"
import React, { useContext, useMemo, useState } from "react"
import Slider from "react-slick"
import styled, { css } from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import { UIContext } from "../../components/shared/layout"
import Block from "../../design-system/atoms/layout-primitives/block"
import Grid from "../atoms/layout-primitives/grid"
import VariantContainer from "../atoms/variant-container"
import TestimonialCard, {
  ITestimonialCard,
} from "../molecules/testimonial-card"
import TestimonialSelector from "../molecules/testimonial-selector"

export interface ITestimonialCardSlider {
  __typename: "ContentfulTestimonialsBlock"
  testimonials: ITestimonialCard[]
}

const SliderWithBoxShadow = styled(Slider)`
  .slick-slide > div {
    /* left & right margins should be the same as the default grid on mobile */
    margin: 0 20px;
    height: 100%;
  }
  .slick-slide {
    height: inherit;
    margin-top: 8px; /* we have to add a small amount of space for the box shadow */
    margin-bottom: 32px; /* gap between cards and slick dots (leaves room for box-shadow) */
  }
`

export const TestimonialCardSlider = ({
  testimonials,
}: Omit<ITestimonialCardSlider, "__typename">) => {
  const {
    //@ts-ignore
    state: { breakpoint, clientLoaded },
  } = useContext(UIContext)
  const [activeId, setActiveId] = useState(
    testimonials?.[0].contentful_id || ""
  )
  const reversedTestimonials = useMemo(
    () => testimonials.slice(0).reverse(),
    [testimonials]
  )
  const sortedTestimonials =
    breakpoint === "lg" ? reversedTestimonials : testimonials
  return (
    <Grid>
      <Block
        variants={{
          lg: css`
            @media (min-width: ${Breakpoints.lgPlus}) {
              grid-column: 2/-2;
            }
          `,
        }}
      >
        {breakpoint === "sm" ||
        (breakpoint === "md" &&
          testimonials[0].image === (null || undefined)) ? (
          clientLoaded && (
            <SliderWithBoxShadow dots>
              {testimonials.map((testimonial, index) => (
                <VariantContainer
                  variants={[
                    css`
                      box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
                    `,
                  ]}
                >
                  <TestimonialCard key={index} {...testimonial} />
                </VariantContainer>
              ))}
            </SliderWithBoxShadow>
          )
        ) : (
          <>
            <VariantContainer
              variants={[
                css`
                  // never show selectors on mobile
                  display: none;
                `,
                css`
                  margin-bottom: 32px;
                  display: flex;
                  flex-wrap: wrap;

                  justify-content: center;
                  gap: 8px 24px;
                `,
                css`
                  display: flex;
                  flex-wrap: wrap;
                  flex-flow: row-reverse wrap-reverse;

                  @media (min-width: ${Breakpoints.lgPlus}) {
                    gap: 24px 32px;
                  }
                `,
              ]}
            >
              {sortedTestimonials.map((testimonial, index) => (
                <TestimonialSelector
                  key={index}
                  logoImage={testimonial.logos?.[0].fluid}
                  onClick={() => setActiveId(testimonial.contentful_id)}
                  active={testimonial.contentful_id === activeId}
                />
              ))}
            </VariantContainer>
            {sortedTestimonials.map((testimonial, index) => (
              <VariantContainer
                variants={[
                  css`
                    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
                  `,
                ]}
                key={index}
              >
                <TestimonialCard
                  {...testimonial}
                  active={testimonial.contentful_id === activeId}
                />
              </VariantContainer>
            ))}
          </>
        )}
      </Block>
    </Grid>
  )
}

export const TESTIMONIAL_SLIDER_TYPENAME = "ContentfulTestimonialsBlock"
export const TestimonialCardSliderFragment = graphql`
  fragment ContentfulTestimonialsBlock on ContentfulTestimonialsBlock {
    __typename
    testimonials {
      ... on ContentfulTestimonial {
        ...ContentfulTestimonial
      }
    }
  }
`
export default TestimonialCardSlider
